// useScrambleText.js
import { useLayoutEffect, useEffect, useRef } from "react";
import { gsap } from "gsap";
// import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin"; // se ce l'hai
// gsap.registerPlugin(ScrambleTextPlugin);

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*?+-_";
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const durationByLength = (len, { min = 0.6, max = 1.2, softCap = 260, sweetSpot = 40 } = {}) => {
	const t = clamp(1 - (len - sweetSpot) / (softCap - sweetSpot), 0, 1);
	return Number((min + t * (max - min)).toFixed(2));
};

/**
 * Scramble del textContent su `ref`:
 * - parte quando cambia `text`
 * - sfx: { playTypeTick, playTypeEnd } opzionale
 * - sfxEnabled: se false, non riproduce alcun suono (live, anche a runtime)
 * - tickEvery: fallback-only, tick ogni N lettere rivelate
 */
export function useScrambleText(
	ref,
	text,
	{ chars = CHARS, durationOpts, sfx, sfxEnabled = true, tickEvery = 2 } = {}
) {
	const tlRef = useRef(null);
	const tickCallsRef = useRef([]);
	const sfxEnabledRef = useRef(sfxEnabled);

	// 🔄 aggiorna il gate anche a runtime (senza riavviare l'animazione)
	useEffect(() => {
		sfxEnabledRef.current = sfxEnabled;
	}, [sfxEnabled]);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;

		// cleanup
		tlRef.current?.kill();
		tickCallsRef.current.forEach(dc => dc.kill?.());
		tickCallsRef.current = [];

		const nextText = (text ?? "").toString();

		// a11y
		if (typeof window !== "undefined" &&
			window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
			el.textContent = nextText;
			return;
		}

		const duration = durationByLength(nextText.length, durationOpts);

		// helper per suoni “sicuri”
		const safeTick = () => { if (sfxEnabledRef.current) sfx?.playTypeTick?.(); };
		const safeEnd = () => { if (sfxEnabledRef.current) sfx?.playTypeEnd?.(); };

		if (gsap.plugins?.ScrambleTextPlugin) {
			// --- Con plugin
			el.textContent = "";
			const tl = gsap.to(el, {
				duration,
				ease: "expo.out",
				scrambleText: { text: nextText, chars, revealDelay: 0, tweenLength: false },
				onComplete: safeEnd,
			});
			tlRef.current = tl;

			// tick distribuiti sulla durata
			if (sfx?.playTypeTick) {
				let t = 0;
				while (t < duration) {
					const step = gsap.utils.random(0.03, 0.06);
					tickCallsRef.current.push(gsap.delayedCall(t, safeTick));
					t += step;
				}
			}

			return () => {
				tl.kill();
				tickCallsRef.current.forEach(dc => dc.kill?.());
				tickCallsRef.current = [];
			};
		}

		// --- Fallback custom
		const charset = chars;
		const rand = () => charset[(Math.random() * charset.length) | 0];
		const len = nextText.length;
		const proxy = { reveal: 0 };
		let lastInt = -1;

		const build = (reveal) => {
			const r = Math.floor(reveal);
			let out = "";
			for (let i = 0; i < len; i++) {
				const ch = nextText[i];
				out += (i < r || ch === " " || ch === "\n") ? ch : rand();
			}
			return out;
		};

		el.textContent = build(0);

		const tl = gsap.to(proxy, {
			reveal: len,
			duration,
			ease: "expo.out",
			onUpdate: () => {
				el.textContent = build(proxy.reveal);
				const cur = Math.floor(proxy.reveal);
				if (cur !== lastInt && cur % tickEvery === 0) safeTick();
				lastInt = cur;
			},
			onComplete: () => { el.textContent = nextText; safeEnd(); },
		});

		tlRef.current = tl;
		return () => tl.kill();
	}, [text]); // parte SOLO quando cambia "text"
}
